import { Minus, Plus } from 'lucide-react'

import { MAX_QUANTITY } from '@/features/marketplace/cart'
import { cn } from '@/lib/cn'

/**
 * A number input that is usable with a thumb.
 *
 * Two buttons and a field rather than `<input type="number">` alone: the native spinners
 * are three pixels tall on a phone, which is where most of this shop will be used, and the
 * field stays typeable for someone ordering twenty-four of something.
 *
 * Clamping is deliberately one-sided here -- the lower bound is 1 and the upper is
 * `MAX_QUANTITY`, matching `CheckoutItemInput` on the API, so an out-of-range quantity is
 * refused at the control rather than as a 422 after the address form.
 */
export function QuantityStepper({
  value,
  onChange,
  label,
  className,
}: {
  value: number
  onChange: (quantity: number) => void
  /** For screen readers -- the field itself carries no visible label. */
  label: string
  className?: string
}) {
  const set = (next: number) => onChange(Math.max(1, Math.min(MAX_QUANTITY, Math.floor(next))))

  const buttonClass =
    'flex h-9 w-9 items-center justify-center text-slate-500 transition-colors ' +
    'hover:bg-slate-100 hover:text-slate-900 disabled:pointer-events-none disabled:opacity-40 ' +
    'dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'

  return (
    <div
      className={cn(
        'inline-flex items-center overflow-hidden rounded-md border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900',
        className,
      )}
    >
      <button
        type="button"
        className={buttonClass}
        onClick={() => set(value - 1)}
        disabled={value <= 1}
        aria-label={`Decrease ${label.toLowerCase()}`}
      >
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={1}
        max={MAX_QUANTITY}
        value={value}
        aria-label={label}
        onChange={(event) => {
          const next = Number(event.target.value)
          // An empty field mid-typing parses as 0; leave the value alone rather than
          // snapping to 1 under the cursor.
          if (Number.isFinite(next) && next > 0) set(next)
        }}
        className="h-9 w-12 border-x border-slate-300 bg-transparent text-center text-sm text-slate-900 [appearance:textfield] focus:outline-none dark:border-slate-700 dark:text-slate-100 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button
        type="button"
        className={buttonClass}
        onClick={() => set(value + 1)}
        disabled={value >= MAX_QUANTITY}
        aria-label={`Increase ${label.toLowerCase()}`}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}
